import React, { useEffect, useState } from "react";
import MiniMap from "./MiniMap";
import axios from "axios";
import {
  PhoneArrowUpRightIcon,
  EnvelopeIcon,
  MapPinIcon,
  InformationCircleIcon,
  ArrowPathIcon,
  UserGroupIcon,
  BriefcaseIcon,
  CalendarDaysIcon,
  ArrowsRightLeftIcon,
  PaperAirplaneIcon,
  ClockIcon,
} from "@heroicons/react/24/solid";
import { Tooltip } from "@material-tailwind/react";
import moment from "moment";
import AddressInput from "./AdressInput";

const SimulateurTaxi = ({ widgetId }) => {
  const [widgetData, setWidgetData] = useState({
    startAddress: "",
    endAddress: "",
    passengers: 1,
    baggage: 0,
    departureTime: "",
    allerRetourIsActive: true,
  });

  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [distance, setDistance] = useState(null);
  const [isRoundTrip, setIsRoundTrip] = useState(false);
  const [widgets, setWidgets] = useState(null);

  const [startCoordinates, setStartCoordinates] = useState(null);
  const [endCoordinates, setEndCoordinates] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [showEditButton, setShowEditButton] = useState(false);
  const [fieldError, setFieldError] = useState(false);

  useEffect(() => {
    fetchWidget();
  }, [widgetId]);

  const fetchWidget = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/widgets/taxi/getOneWidget/${widgetId}`
      );

      if (response.status === 200) {
        const data = response.data;
        setWidgets(data.widget);
      } else {
        console.error("Erreur lors de la récupération des widgets");
      }
    } catch (error) {
      if (
        error.response &&
        (error.response.status === 404 || error.response.status === 500)
      ) {
        setIsError(true);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setWidgetData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldError(false);

    try {
      setIsLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/widgets/taxi/simulate/${widgetId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ...widgetData, isRoundTrip }),
        }
      );

      if (response.ok) {
        const { distance, price, startCoordinatesData, endCoordinatesData } =
          await response.json();

        setStartCoordinates(startCoordinatesData);
        setEndCoordinates(endCoordinatesData);
        setCalculatedPrice(price);
        setDistance(distance);
        setShowForm(false);
        setShowEditButton(true);
      } else {
        console.error("Erreur lors de la simulation");
        setFieldError(true);
      }
    } catch (error) {
      console.error("Erreur:", error);
      setFieldError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const createEmailContent = () => {
    const emailSubject = "Demande de réservation de course - Simulateur";
    const emailBody = `
      Bonjour,

      Je viens de simuler une course sur votre site et je souhaite obtenir plus d'informations, ainsi qu'une validation du tarif, pour le trajet suivant :

      Aller simple ou aller-retour : ${
        isRoundTrip ? "Aller-retour" : "Aller simple"
      }
      Nombre de passagers : ${widgetData?.passengers}
      Nombre de bagages : ${widgetData?.baggage}
      Adresse de départ : ${widgetData?.startAddress}
      Adresse d'arrivée : ${widgetData?.endAddress}
      Date et heure souhaitées : ${moment
        .utc(widgetData?.departureTime)
        .format("DD-MM-YYYY HH:mm")}

      Prix de la course estimé : ${calculatedPrice} €
      Km de la course : ${distance} km


      Voici mes coordonnées de contact :

      Nom : (A remplir)
      Prénom : (A remplir)
      Téléphone : (+33) (A remplir)
      E-Mail: (A remplir)

      Dans l'attente de votre réponse,
      Cordialement,

      Demande de devis obtenue grâce à votre simulateur de course Local.fr
    `;

    return `mailto:${widgets?.contactMail}?subject=${encodeURIComponent(
      emailSubject
    )}&body=${encodeURIComponent(emailBody)}`;
  };

  const handleEditSimulation = () => {
    setShowForm(true);
    setShowEditButton(false);
  };

  const formatPhone = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/[^\d]/g, "");
    if (cleaned.startsWith("0")) {
      return `+33${cleaned.slice(1)}`;
    }
    return `+${cleaned}`;
  };

  const recordButtonClick = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/widgets/taxi/recordClick/${widgetId}`,
        { clickedAt: new Date() }
      );
    } catch (error) {
      console.error("Erreur lors de l'enregistrement du clic :", error);
    }
  };

  const inputClass =
    "widget-input w-full border border-gray-200 rounded-xl px-3 py-2.5 text-gray-800 text-sm transition-shadow bg-white";

  return (
    <>
      {isError ? (
        <div className="flex items-center justify-center h-screen w-screen antialiased text-center bg-stone-100 text-gray-800 font-openSans">
          <article className="mx-auto max-w-screen-md p-6 sm:p-12 items-center">
            <h3 className="text-2xl sm:text-3xl font-light mb-8 sm:mb-12">
              Le simulateur est désactivé !
            </h3>
            <div>
              <p className="text-lg sm:text-xl text-gray-800 mb-8">
                Merci de contacter notre support{" "}
                <a
                  className="text-blue-800 hover:text-blue-700 mb-8"
                  href="https://local.fr"
                >
                  Local.fr
                </a>{" "}
                pour plus d'informations.
              </p>
              <p className="font-bold">Simulateur Taxi-VTC avec Local.fr</p>
            </div>
          </article>
        </div>
      ) : (
        <div
          className="min-h-[400px] w-full rounded-xl overflow-hidden"
          style={{
            backgroundColor: widgets?.backgroundColor,
            color: widgets?.labelColor,
            "--widget-color": widgets?.btnColor,
          }}
        >
          {showForm ? (
            <form onSubmit={handleSubmit} className="p-4 sm:p-6">
              {/* Title */}
              <div className="mb-5 sm:mb-6">
                <h2 className="text-xl sm:text-2xl font-bold">
                  Simulateur de course
                </h2>
                <p className="text-xs sm:text-sm opacity-70 mt-1">
                  Estimez le tarif de votre trajet en quelques clics
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:gap-4">
                {/* Address fields */}
                <AddressInput
                  label="Adresse de départ"
                  color={widgets}
                  initialValue={widgetData.startAddress}
                  setFieldValue={(value, lat, lng) =>
                    setWidgetData((prev) => ({
                      ...prev,
                      startAddress: value,
                      startLat: lat,
                      startLng: lng,
                    }))
                  }
                />
                <AddressInput
                  label="Adresse d'arrivée"
                  color={widgets}
                  initialValue={widgetData.endAddress}
                  setFieldValue={(value, lat, lng) =>
                    setWidgetData((prev) => ({
                      ...prev,
                      endAddress: value,
                      endLat: lat,
                      endLng: lng,
                    }))
                  }
                />

                {/* Date & time */}
                <div>
                  <label
                    htmlFor="departureTime"
                    className="flex items-center gap-1.5 text-sm font-medium mb-1.5"
                  >
                    <CalendarDaysIcon className="w-4 h-4 opacity-70" />
                    Date et heure de départ
                  </label>
                  <input
                    type="datetime-local"
                    id="departureTime"
                    name="departureTime"
                    value={widgetData.departureTime}
                    onChange={handleInputChange}
                    className={inputClass}
                    required
                  />
                </div>

                {/* Passengers + Baggage row */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label
                      htmlFor="passengers"
                      className="flex items-center gap-1.5 text-sm font-medium mb-1.5"
                    >
                      <UserGroupIcon className="w-4 h-4 opacity-70" />
                      Passagers
                    </label>
                    <select
                      id="passengers"
                      name="passengers"
                      value={widgetData.passengers}
                      onChange={handleInputChange}
                      className={inputClass}
                      required
                    >
                      {[...Array(10).keys()].map((num) => (
                        <option key={num + 1} value={num + 1}>
                          {num + 1}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="baggage"
                      className="flex items-center gap-1.5 text-sm font-medium mb-1.5"
                    >
                      <BriefcaseIcon className="w-4 h-4 opacity-70" />
                      Bagages
                    </label>
                    <select
                      id="baggage"
                      name="baggage"
                      value={widgetData.baggage}
                      onChange={handleInputChange}
                      className={inputClass}
                      required
                    >
                      {[...Array(11).keys()].map((num) => (
                        <option key={num} value={num}>
                          {num}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Round trip toggle */}
                {widgets?.allerRetourIsActive && (
                  <div
                    className="flex items-center justify-between p-3 rounded-xl border border-white/20"
                    style={{ backgroundColor: "rgba(255,255,255,0.08)" }}
                  >
                    <label
                      htmlFor="isRoundTrip"
                      className="flex items-center gap-1.5 text-sm font-medium cursor-pointer"
                    >
                      <ArrowsRightLeftIcon className="w-4 h-4 opacity-70" />
                      Aller-retour
                    </label>
                    <input
                      type="checkbox"
                      id="isRoundTrip"
                      name="isRoundTrip"
                      checked={isRoundTrip}
                      onChange={() => setIsRoundTrip(!isRoundTrip)}
                      className="peer sr-only opacity-0"
                    />
                    <label
                      htmlFor="isRoundTrip"
                      className="relative flex h-6 w-11 cursor-pointer items-center rounded-full bg-gray-400 px-0.5 transition-colors before:h-5 before:w-5 before:rounded-full before:bg-white before:shadow before:transition-transform before:duration-300 peer-checked:bg-green-500 peer-checked:before:translate-x-full"
                    >
                      <span className="sr-only">Enable</span>
                    </label>
                  </div>
                )}

                {/* Error message */}
                {fieldError && (
                  <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-xl p-3 flex items-center gap-2">
                    <InformationCircleIcon className="w-5 h-5 shrink-0" />
                    <span>
                      Impossible de calculer le trajet. Vérifiez les adresses et
                      réessayez.
                    </span>
                  </div>
                )}

                {/* Submit button */}
                <button
                  className="w-full py-3 px-4 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-60 mt-1"
                  style={{
                    backgroundColor: widgets?.btnColor || "#000000",
                    color: widgets?.labelColor || "#ffffff",
                  }}
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      Calcul en cours...
                    </>
                  ) : (
                    <>
                      <PaperAirplaneIcon className="w-4 h-4" />
                      Calculer le prix
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <>
              {calculatedPrice !== null && (
                <div className="flex flex-col w-full p-4 sm:p-5">

                  {/* Title */}
                  <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-5 text-left">
                    Récapitulatif de votre course
                  </h2>

                  {/* === TIMELINE === */}
                  <div className="flex gap-3 mb-5 sm:mb-6">

                    {/* Rail */}
                    <div className="flex flex-col items-center shrink-0 w-4 pt-0.5">
                      <div className="w-3.5 h-3.5 rounded-full border-[2.5px] border-current shrink-0 opacity-70" />
                      <div className="w-[2px] flex-1 bg-current opacity-15" />
                      <div className="w-2.5 h-2.5 rounded-full bg-current shrink-0 opacity-25 my-0.5" />
                      <div className="w-[2px] flex-1 bg-current opacity-15" />
                      <div className="w-3.5 h-3.5 rounded-full bg-current shrink-0 opacity-70" />
                    </div>

                    {/* Steps */}
                    <div className="flex flex-col gap-3 min-w-0 flex-1">

                      {/* Départ */}
                      <div className="text-left">
                        <p className="text-[10px] uppercase tracking-widest opacity-40 font-bold text-left">
                          Prise en charge
                        </p>
                        <p className="text-[13px] sm:text-sm font-semibold leading-snug break-words mt-0.5 text-left">
                          {widgetData.startAddress}
                        </p>
                        {widgetData.departureTime && (
                          <div className="flex items-center gap-1.5 mt-1.5 opacity-50">
                            <ClockIcon className="w-3 h-3 shrink-0" />
                            <span className="text-[11px] sm:text-xs">
                              {moment.utc(widgetData.departureTime).format("DD/MM/YYYY")} à {moment.utc(widgetData.departureTime).format("HH:mm")}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Map */}
                      {startCoordinates && endCoordinates && (
                      <div className="rounded-xl overflow-hidden relative">
                        <MiniMap
                          startLatitude={startCoordinates.startCoordinates.latitude}
                          startLongitude={startCoordinates.startCoordinates.longitude}
                          endLatitude={endCoordinates.endCoordinates.latitude}
                          endLongitude={endCoordinates.endCoordinates.longitude}
                        />
                        {/* Info bubbles overlay */}
                        <div className="absolute bottom-2 left-2 right-2 z-[1] flex flex-wrap gap-1">
                          <span className="bg-white/90 backdrop-blur-sm text-gray-800 rounded-full px-2 py-0.5 text-[10px] sm:text-[11px] font-semibold flex items-center gap-1 shadow-sm">
                            <MapPinIcon className="w-2.5 h-2.5 text-gray-500 shrink-0" />
                            {distance} km
                          </span>
                          <span className="bg-white/90 backdrop-blur-sm text-gray-800 rounded-full px-2 py-0.5 text-[10px] sm:text-[11px] font-semibold flex items-center gap-1 shadow-sm">
                            <ArrowsRightLeftIcon className="w-2.5 h-2.5 text-gray-500 shrink-0" />
                            {isRoundTrip ? "A/R" : "Aller simple"}
                          </span>
                          <span className="bg-white/90 backdrop-blur-sm text-gray-800 rounded-full px-2 py-0.5 text-[10px] sm:text-[11px] font-semibold flex items-center gap-1 shadow-sm">
                            <UserGroupIcon className="w-2.5 h-2.5 text-gray-500 shrink-0" />
                            {widgetData.passengers}
                          </span>
                          {widgetData.baggage > 0 && (
                            <span className="bg-white/90 backdrop-blur-sm text-gray-800 rounded-full px-2 py-0.5 text-[10px] sm:text-[11px] font-semibold flex items-center gap-1 shadow-sm">
                              <BriefcaseIcon className="w-2.5 h-2.5 text-gray-500 shrink-0" />
                              {widgetData.baggage}
                            </span>
                          )}
                        </div>
                      </div>
                      )}

                      {/* Destination */}
                      <div className="text-left">
                        <p className="text-[10px] uppercase tracking-widest opacity-40 font-bold text-left">
                          Destination
                        </p>
                        <p className="text-[13px] sm:text-sm font-semibold leading-snug break-words mt-0.5 text-left">
                          {widgetData.endAddress}
                        </p>
                      </div>

                    </div>
                  </div>

                  {/* === PRICE === */}
                  <div
                    className="rounded-2xl p-4 sm:p-5 flex items-center justify-between mb-4"
                    style={{ backgroundColor: widgets?.btnColor }}
                  >
                    <div>
                      <p className="text-xs sm:text-sm font-semibold opacity-90">
                        Prix estimé
                      </p>
                      <Tooltip content="Le prix reste approximatif, merci de prendre contact pour plus d'informations">
                        <span className="text-[10px] sm:text-[11px] opacity-50 cursor-help flex items-center gap-0.5 mt-0.5">
                          <InformationCircleIcon className="h-3 w-3 shrink-0" />
                          Tarif indicatif
                        </span>
                      </Tooltip>
                    </div>
                    <p className="text-3xl sm:text-4xl font-bold price_sim tracking-tight">
                      {calculatedPrice}
                      <span className="text-sm sm:text-base font-medium ml-0.5">€</span>
                    </p>
                  </div>

                  {/* === CTA === */}
                  <div className="flex flex-col gap-2.5 mb-3">
                    <a
                      href={createEmailContent()}
                      onClick={recordButtonClick}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl text-sm font-semibold transition-all hover:opacity-90 active:scale-[0.98]"
                      style={{
                        backgroundColor: widgets?.btnColor || "#000000",
                        color: widgets?.labelColor || "#ffffff",
                      }}
                    >
                      <EnvelopeIcon className="h-4 w-4" />
                      Réserver par e-mail
                    </a>

                    {widgets?.contactPhone && (
                      <a
                        href={`tel:${formatPhone(widgets.contactPhone)}`}
                        target="_parent"
                        className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl text-sm font-semibold border border-white/20 transition-all hover:bg-white/5 active:scale-[0.98]"
                        style={{
                          color: widgets?.labelColor || "#ffffff",
                        }}
                      >
                        <PhoneArrowUpRightIcon className="h-4 w-4" />
                        Appeler pour réserver
                      </a>
                    )}
                  </div>

                  {/* Edit */}
                  {showEditButton && (
                    <button
                      onClick={handleEditSimulation}
                      className="w-full text-xs font-medium opacity-40 hover:opacity-70 transition-opacity flex items-center justify-center gap-1.5 py-1.5"
                    >
                      <ArrowPathIcon className="w-3 h-3" />
                      Modifier la simulation
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default SimulateurTaxi;
